(() => {
  const INITIALIZED = 'dfnUpgradeInitialized';

  function activateTab(tabRoot, index, focus = false) {
    const buttons = [...tabRoot.querySelectorAll('[data-dfn-tab-button]')];
    const panels = [...tabRoot.querySelectorAll('[data-dfn-tab-panel]')];
    const suitability = tabRoot.querySelector('[data-dfn-suitability]');

    buttons.forEach((button) => {
      const active = button.dataset.dfnTabButton === index;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.tabIndex = active ? 0 : -1;
      if (active && focus) {
        button.focus({ preventScroll: true });
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.dfnTabPanel !== index;
    });

    if (suitability) {
      suitability.hidden = index === '998' || index === '999';
    }
  }

  function initializeTabs(scope) {
    scope.querySelectorAll('[data-dfn-tabs]').forEach((tabRoot) => {
      if (tabRoot.dataset[INITIALIZED] === 'true') return;
      tabRoot.dataset[INITIALIZED] = 'true';

      const buttons = [...tabRoot.querySelectorAll('[data-dfn-tab-button]')];
      if (!buttons.length) return;

      activateTab(tabRoot, buttons[0].dataset.dfnTabButton);

      tabRoot.addEventListener('click', (event) => {
        const button = event.target.closest('[data-dfn-tab-button]');
        if (!button || !tabRoot.contains(button)) return;
        activateTab(tabRoot, button.dataset.dfnTabButton);
      });

      tabRoot.addEventListener('keydown', (event) => {
        const current = event.target.closest('[data-dfn-tab-button]');
        if (!current || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        const currentIndex = buttons.indexOf(current);
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % buttons.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        activateTab(tabRoot, buttons[nextIndex].dataset.dfnTabButton, true);
      });
    });
  }

  function initializeAccessoryPagination(scope) {
    scope.querySelectorAll('[data-dfn-accessory-result]').forEach((result) => {
      if (result.dataset.dfnPaginationInitialized === 'true') return;

      const list = result.querySelector('[data-dfn-accessory-list]');
      const pager = result.querySelector('[data-dfn-accessory-pagination]');
      const items = [...(list?.querySelectorAll(':scope > .dfn-accessory-list__item') || [])];
      const pageSize = Math.max(1, Number(result.dataset.dfnAccessoryPageSize) || 3);
      const pageCount = Math.ceil(items.length / pageSize);
      if (!list || !pager || !items.length) return;

      result.dataset.dfnPaginationInitialized = 'true';
      if (window.matchMedia('(max-width: 749px)').matches) {
        items.forEach((item) => { item.hidden = false; });
        pager.hidden = true;
        result.dataset.dfnMobileContinuousScroll = 'true';
        return;
      }
      if (pageCount <= 1) {
        pager.hidden = true;
        items.forEach((item) => { item.hidden = false; });
        return;
      }

      const previous = pager.querySelector('[data-dfn-accessory-prev]');
      const next = pager.querySelector('[data-dfn-accessory-next]');
      const dots = pager.querySelector('[data-dfn-accessory-dots]');
      const status = pager.querySelector('[data-dfn-accessory-page-status]');
      let currentPage = 0;

      const dotButtons = Array.from({ length: pageCount }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'dfn-accessory-pagination__dot';
        dot.setAttribute('aria-label', `Zubehörseite ${index + 1} anzeigen`);
        dot.addEventListener('click', () => renderPage(index));
        dots.appendChild(dot);
        return dot;
      });

      function renderPage(page) {
        currentPage = Math.min(Math.max(page, 0), pageCount - 1);
        const start = currentPage * pageSize;
        const end = start + pageSize;

        items.forEach((item, index) => {
          item.hidden = index < start || index >= end;
        });
        dotButtons.forEach((dot, index) => {
          dot.setAttribute('aria-current', index === currentPage ? 'true' : 'false');
        });
        previous.disabled = currentPage === 0;
        next.disabled = currentPage === pageCount - 1;
        status.textContent = `Seite ${currentPage + 1} von ${pageCount}`;
        list.scrollTo({ left: 0, behavior: 'smooth' });
      }

      previous.addEventListener('click', () => renderPage(currentPage - 1));
      next.addEventListener('click', () => renderPage(currentPage + 1));
      pager.hidden = false;
      renderPage(0);
    });
  }

  class DfnAccessoryRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.load();
    }

    buildUrl(baseUrl) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('product_id', this.dataset.productId);
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url.toString();
    }

    async fetchTemplate(baseUrl) {
      if (!baseUrl) return null;
      const response = await fetch(this.buildUrl(baseUrl), { credentials: 'same-origin' });
      if (!response.ok) return null;

      const html = document.createElement('div');
      html.innerHTML = await response.text();
      return html.querySelector('template[data-dfn-accessory-response]');
    }

    finish(state) {
      this.dataset.state = state;
      this.dispatchEvent(new CustomEvent('dfn-accessories:updated', { bubbles: true }));
    }

    async load() {
      this.dataset.state = 'loading';

      try {
        let template = await this.fetchTemplate(this.dataset.complementaryUrl);
        if (!template) template = await this.fetchTemplate(this.dataset.relatedUrl);

        if (template?.content?.querySelector('.dfn-accessory-result')) {
          this.replaceChildren(template.content.cloneNode(true));
          initializeAccessoryPagination(this);
          this.finish('loaded');
          return;
        }
      } catch (error) {
        console.warn('DFN accessory recommendations could not be loaded.', error);
      }

      this.replaceChildren();
      this.finish('empty');
    }
  }

  if (!customElements.get('dfn-accessory-recommendations')) {
    customElements.define('dfn-accessory-recommendations', DfnAccessoryRecommendations);
  }

  function updateAccessoryState(card) {
    const grid = card.closest('[data-dfn-details-grid]');
    const loaders = [...card.querySelectorAll('[data-dfn-accessory-loader]')];
    const isLoading = loaders.some((loader) => !loader.dataset.state || loader.dataset.state === 'loading');
    const hasContent = Boolean(card.querySelector('aside'));
    const isEmpty = !hasContent && !isLoading;

    card.classList.toggle('dfn-accessory-card--loading', isLoading);
    card.classList.toggle('dfn-accessory-card--empty', isEmpty);
    grid?.classList.toggle('dfn-details-grid--without-accessories', isEmpty);
  }

  function initializeAccessoryCards(scope) {
    scope.querySelectorAll('[data-dfn-accessory-card]').forEach((card) => {
      if (card.dataset.dfnAccessoryObserved === 'true') return;
      card.dataset.dfnAccessoryObserved = 'true';
      updateAccessoryState(card);

      card.addEventListener('dfn-accessories:updated', () => updateAccessoryState(card));
      new MutationObserver(() => updateAccessoryState(card)).observe(card, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-state'],
      });
    });
  }

  function initializeMediaDots(scope) {
    scope.querySelectorAll('[data-dfn-media-dots]').forEach((counter) => {
      if (counter.dataset.dfnMediaDotsInitialized === 'true') return;
      counter.dataset.dfnMediaDotsInitialized = 'true';

      const current = counter.querySelector('[data-dfn-media-current]');
      const dots = [...counter.querySelectorAll('[data-dfn-media-dot]')];
      const status = counter.querySelector('[data-dfn-media-status]');
      if (!current || !dots.length) return;

      const update = () => {
        const index = Math.min(Math.max(Number.parseInt(current.textContent, 10) || 1, 1), dots.length);
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex + 1 === index));
        if (status) status.textContent = `Bild ${index} von ${dots.length}`;
      };

      update();
      new MutationObserver(update).observe(current, { childList: true, characterData: true, subtree: true });
    });
  }

  function initializeThumbnailSliderArrows(scope) {
    scope.querySelectorAll('slider-component.thumbnail-slider').forEach((component) => {
      const slider = component.querySelector('[id^="Slider-Thumbnails-"]');
      const previous = component.querySelector('button[name="previous"]');
      const next = component.querySelector('button[name="next"]');
      if (!slider || !previous || !next) return;

      const update = () => {
        const maximum = Math.max(0, slider.scrollWidth - slider.clientWidth);
        const position = Math.max(0, slider.scrollLeft);
        const tolerance = 2;
        const atStart = maximum <= tolerance || position <= tolerance;
        const atEnd = maximum <= tolerance || position >= maximum - tolerance;

        previous.disabled = atStart;
        next.disabled = atEnd;
        previous.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
        previous.classList.toggle('is-dfn-disabled', atStart);
        next.classList.toggle('is-dfn-disabled', atEnd);
      };

      if (component.dataset.dfnThumbnailArrowsInitialized !== 'true') {
        component.dataset.dfnThumbnailArrowsInitialized = 'true';
        let frame = 0;
        const scheduleUpdate = () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(update);
        };

        slider.addEventListener('scroll', scheduleUpdate, { passive: true });
        new ResizeObserver(scheduleUpdate).observe(slider);
        new MutationObserver(scheduleUpdate).observe(slider, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'hidden', 'style'],
        });
      }

      requestAnimationFrame(update);
      window.setTimeout(update, 250);
    });
  }

  function getSelectedVariant(productInfo) {
    const selectedVariantJson = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent?.trim();
    if (!selectedVariantJson) return null;
    try {
      return JSON.parse(selectedVariantJson);
    } catch (error) {
      console.warn('DFN selected variant JSON could not be read.', error);
      return null;
    }
  }

  function updateVariantIdentifiers(productInfo, variantOverride = null) {
    const variant = variantOverride || getSelectedVariant(productInfo);
    if (!variant) return;

    const skuWrapper = productInfo.querySelector('[data-dfn-sku-wrapper]');
    const skuValue = productInfo.querySelector('[data-dfn-sku]');

    const sku = variant.sku && !['-1', '-2'].includes(String(variant.sku)) ? String(variant.sku) : '';

    if (skuWrapper && skuValue) {
      skuValue.textContent = sku;
      skuWrapper.hidden = !sku;
    }
  }

  function normalizeVariantToken(value) {
    return String(value || '')
      .toLocaleLowerCase('de-DE')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s\-_.\/&()]/g, '');
  }

  function readBestsellerRules(productInfo) {
    const scripts = [...productInfo.querySelectorAll('[data-dfn-bestseller-rules]')];
    if (!scripts.length) return [];

    const rules = [];
    scripts.forEach((script) => {
      try {
        const parsed = JSON.parse(script.textContent || '[]');
        if (Array.isArray(parsed)) rules.push(...parsed.filter(Boolean));
      } catch (error) {
        console.warn('DFN bestseller rules could not be read.', error);
      }
    });
    const seen = new Set();
    return rules.filter((rule) => {
      const key = JSON.stringify(rule);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function selectedOptionValues(productInfo) {
    const selected = new Map();
    productInfo.querySelectorAll('[data-dfn-option-name]').forEach((group) => {
      const name = group.dataset.dfnOptionName || '';
      const checked = group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select');
      const value = checked?.value || select?.value || '';
      if (name && value) selected.set(normalizeVariantToken(name), normalizeVariantToken(value));
    });
    return selected;
  }

  function variantTokensMatch(actual, expected) {
    if (!expected) return true;
    if (!actual) return false;
    return actual === expected || actual.includes(expected) || expected.includes(actual);
  }

  function refreshBestsellerBadges(productInfo) {
    const rules = readBestsellerRules(productInfo);
    if (!rules.length) return;

    productInfo.querySelectorAll('[data-dfn-bestseller-badge]').forEach((badge) => badge.remove());
    productInfo.querySelectorAll('[data-dfn-variant-card]').forEach((card) => {
      card.classList.remove('dfn-variant-card--has-badge', 'dfn-variant-swatch--has-badge');
    });

    const selected = selectedOptionValues(productInfo);
    const cards = [...productInfo.querySelectorAll('[data-dfn-variant-card]')];

    rules.forEach((rule) => {
      const conditionName = normalizeVariantToken(rule.conditionOptionName);
      const conditionValue = normalizeVariantToken(rule.conditionOptionValue);
      const targetName = normalizeVariantToken(rule.targetOptionName);
      const targetValue = normalizeVariantToken(rule.targetOptionValue);
      const selectedCondition = conditionName ? selected.get(conditionName) : '';

      if (conditionName && !selected.has(conditionName)) return;
      if (conditionValue && !variantTokensMatch(selectedCondition, conditionValue)) return;
      if (!targetValue) return;

      const targetCards = cards.filter((card) => {
        const cardName = normalizeVariantToken(card.dataset.dfnOptionName);
        const cardValue = normalizeVariantToken(card.dataset.dfnOptionValue);
        return (!targetName || cardName === targetName) && variantTokensMatch(cardValue, targetValue);
      });

      targetCards.forEach((targetCard) => {
        if (targetCard.querySelector('[data-dfn-bestseller-badge]')) return;

        const isSwatch = targetCard.classList.contains('dfn-variant-swatch');

        const stack = document.createElement('span');
        stack.className = isSwatch ? 'dfn-variant-swatch__badge-stack' : 'dfn-variant-card__badge-stack';
        stack.dataset.dfnBestsellerBadge = 'true';
        stack.setAttribute('aria-hidden', 'true');

        const badge = document.createElement('span');
        badge.className = isSwatch
          ? 'dfn-variant-card__badge dfn-variant-swatch__badge'
          : 'dfn-variant-card__badge';
        badge.textContent = String(rule.badgeText || 'Bestseller');
        stack.appendChild(badge);
        targetCard.prepend(stack);
        targetCard.classList.add(isSwatch ? 'dfn-variant-swatch--has-badge' : 'dfn-variant-card--has-badge');
      });
    });
  }

  function compactEuroSpacing(value) {
    return String(value ?? '').replace(/[\s\u00a0\u202f]+€/g, '€');
  }

  function formatSubscriptionMoney(cents, currency = 'EUR') {
    if (!Number.isFinite(Number(cents))) return '';
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents) / 100);
    return currency === 'EUR' ? compactEuroSpacing(formatted) : formatted;
  }

  function compactSubscriptionEuroNodes(scope) {
    if (!scope?.querySelectorAll) return;
    const selectors = [
      '[data-dfn-subscription-price]',
      '[data-dfn-subscription-compare]',
      '.bs-item-label-price',
      '.bs-item-label-compare-price',
      '.bs-stack-item-label-price',
      '.bs-stack-item-label-compare-price',
      '.bs-stack-item-right',
    ].join(',');

    scope.querySelectorAll(selectors).forEach((node) => {
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      let textNode = walker.nextNode();
      while (textNode) {
        textNode.nodeValue = compactEuroSpacing(textNode.nodeValue);
        textNode = walker.nextNode();
      }
    });
  }

  function refreshSubscriptionPreview(productInfo, variantOverride = null) {
    const variant = variantOverride || getSelectedVariant(productInfo);
    if (!variant) return;

    productInfo.querySelectorAll('[data-dfn-subscription-preview]').forEach((preview) => {
      const dataScript = preview.querySelector('[data-dfn-subscription-variant-prices]');
      if (!dataScript) return;

      let prices = [];
      try {
        prices = JSON.parse(dataScript.textContent || '[]');
      } catch (error) {
        console.warn('DFN subscription prices could not be read.', error);
        return;
      }

      const current = prices.find((item) => String(item.id) === String(variant.id));
      if (!current) return;

      const currency = preview.dataset.dfnCurrency || window.Shopify?.currency?.active || 'EUR';
      const regularPrice = Number(current.price);
      const subscriptionPrice = Number(current.subscriptionPrice);
      const priceNode = preview.querySelector('[data-dfn-subscription-price]');
      const compareNode = preview.querySelector('[data-dfn-subscription-compare]');

      if (priceNode) priceNode.textContent = formatSubscriptionMoney(subscriptionPrice, currency);
      if (compareNode) {
        compareNode.textContent = Number.isFinite(regularPrice) ? `statt ${formatSubscriptionMoney(regularPrice, currency)}` : '';
      }
    });
  }

  function formatBonyPrice(value) {
    if (!value) return '';
    return compactEuroSpacing(value.replace(/\s+/g, ' ').replace(/\s*€?$/, '€').trim());
  }

  function extractBonyPrices(item) {
    if (!item) return [];
    const matches = item.textContent.match(/(?:\d{1,3}(?:[.\s]\d{3})*|\d+),\d{2}\s*€?/g) || [];
    return [...new Set(matches.map(formatBonyPrice))];
  }

  function identifyBonyItems(wrapper) {
    const items = [...wrapper.querySelectorAll('.bs-item')];
    if (items.length < 2) return null;

    const normalise = (node) => node.textContent.toLowerCase().replace(/\s+/g, ' ').trim();
    const oneTime = items.find((item) => /einzel|einmal|one[\s-]?time/.test(normalise(item))) || null;
    const subscriptions = items.filter((item) => item !== oneTime);
    if (!subscriptions.length) return null;

    // Bony kann mehrere Abo-Laufzeiten ausgeben (z. B. 3 / 6 / 12 Monate).
    // Wichtig: Die DOM-Reihenfolge darf hier NICHT verändert werden, sonst kann ein
    // MutationObserver die Optionen endlos neu sortieren und die Anzeige flackert.
    const subscription =
      subscriptions.find((item) => nativeBonyItemIsSelected(item)) ||
      subscriptions.find((item) => /monat|abo|abonnement|subscribe|liefer.*spar/.test(normalise(item))) ||
      subscriptions[0];

    return { items, oneTime, subscription, subscriptions };
  }

  function verticallyCenterNativeSubscriptionOptions(panel) {
    panel.querySelectorAll(
      '.dfn-native-subscription-option, .dfn-native-one-time-option'
    ).forEach((option) => {
      option.style.setProperty('display', 'flex', 'important');
      option.style.setProperty('flex-direction', 'column', 'important');
      option.style.setProperty('justify-content', 'center', 'important');
      option.style.setProperty('align-items', 'stretch', 'important');
      option.style.setProperty('align-content', 'normal', 'important');

      const top = option.querySelector(':scope > .bs-stack-item-top')
        || option.querySelector('.bs-stack-item-top');
      if (top) {
        top.style.setProperty('width', '100%', 'important');
        top.style.setProperty('min-height', '0', 'important');
        top.style.setProperty('height', 'auto', 'important');
        top.style.setProperty('margin-top', '0', 'important');
        top.style.setProperty('margin-bottom', '0', 'important');
        top.style.setProperty('align-self', 'auto', 'important');
      }

      option.querySelectorAll('.bs-stack-item-left, .bs-stack-item-right').forEach((column) => {
        column.style.setProperty('min-height', '0', 'important');
        column.style.setProperty('height', 'auto', 'important');
        column.style.setProperty('align-items', 'center', 'important');
      });
    });
  }

  function nativeBonyItemIsSelected(item) {
    if (!item) return false;
    return item.dataset.selected === 'true' || item.getAttribute('aria-checked') === 'true' || Boolean(item.querySelector('input:checked'));
  }

  function selectNativeBonyItem(item) {
    if (!item) return;
    const control = item.querySelector('input[type="radio"], input[type="checkbox"]');
    if (control && !control.checked) {
      control.click();
      return;
    }
    const clickable = item.querySelector('label, button, [role="radio"]') || item;
    clickable.click();
  }

  function buildBonyFacade(host, wrapper) {
    const facade = document.createElement('section');
    facade.className = 'dfn-bony-facade';
    facade.setAttribute('aria-label', 'Abo-Option');
    facade.innerHTML = `
      <div class="dfn-bony-facade__content">
        <div class="dfn-bony-facade__heading">
          <h3 data-dfn-bony-title>Monatlich liefern & sparen</h3>
          <span class="dfn-bony-facade__recommendation">Empfohlen</span>
        </div>
        <ul class="dfn-bony-facade__benefits">
          <li>Regelmäßig & bequem nach Hause</li>
          <li>Jederzeit kündbar</li>
          <li data-dfn-bony-discount-benefit>Rabatt auf jede Lieferung</li>
        </ul>
      </div>
      <div class="dfn-bony-facade__aside">
        <strong class="dfn-bony-facade__price" data-dfn-bony-price></strong>
        <span class="dfn-bony-facade__compare" data-dfn-bony-compare></span>
        <button type="button" class="dfn-bony-facade__button" data-dfn-bony-toggle aria-expanded="false">
          <span data-dfn-bony-button-text>Abo auswählen</span>
          <svg viewBox="0 0 12 8" aria-hidden="true"><path d="m1 1.5 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
      <button type="button" class="dfn-bony-facade__one-time" data-dfn-bony-one-time hidden>Stattdessen Einzelkauf wählen</button>
    `;

    host.insertBefore(facade, wrapper);
    wrapper.classList.add('dfn-bony-native');
    host.classList.add('dfn-bony-host');

    const toggle = facade.querySelector('[data-dfn-bony-toggle]');
    const oneTimeButton = facade.querySelector('[data-dfn-bony-one-time]');

    toggle.addEventListener('click', () => {
      const identified = identifyBonyItems(wrapper);
      if (!identified) return;
      if (!nativeBonyItemIsSelected(identified.subscription)) {
        selectNativeBonyItem(identified.subscription);
      }
      host.classList.toggle('is-dfn-bony-expanded');
      toggle.setAttribute('aria-expanded', host.classList.contains('is-dfn-bony-expanded') ? 'true' : 'false');
      window.setTimeout(() => refreshBonyFacade(host, wrapper, facade), 80);
    });

    oneTimeButton.addEventListener('click', () => {
      const identified = identifyBonyItems(wrapper);
      if (!identified) return;
      selectNativeBonyItem(identified.oneTime);
      host.classList.remove('is-dfn-bony-expanded');
      toggle.setAttribute('aria-expanded', 'false');
      window.setTimeout(() => refreshBonyFacade(host, wrapper, facade), 80);
    });

    let refreshFrame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(refreshFrame);
      refreshFrame = requestAnimationFrame(() => refreshBonyFacade(host, wrapper, facade));
    }).observe(wrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-selected', 'aria-checked', 'checked'],
    });

    refreshBonyFacade(host, wrapper, facade);
  }

  function refreshBonyFacade(host, wrapper, facade) {
    const identified = identifyBonyItems(wrapper);
    if (!identified) return;

    const { oneTime, subscription } = identified;
    const subscriptionSelected = nativeBonyItemIsSelected(subscription);
    const subscriptionText = subscription.textContent.replace(/\s+/g, ' ').trim();
    const discountMatch = subscriptionText.match(/(\d{1,2})\s*%/);
    const discount = discountMatch?.[1] || '5';
    const subscriptionPrices = extractBonyPrices(subscription);
    const oneTimePrices = extractBonyPrices(oneTime);
    const subscriptionPrice = subscriptionPrices[subscriptionPrices.length - 1] || '';
    const comparePrice = subscriptionPrices.length > 1 ? subscriptionPrices[0] : (oneTimePrices[oneTimePrices.length - 1] || '');

    facade.querySelector('[data-dfn-bony-title]').textContent = `Monatlich liefern & ${discount}% sparen`;
    facade.querySelector('[data-dfn-bony-discount-benefit]').textContent = `${discount}% Rabatt auf jede Lieferung`;
    facade.querySelector('[data-dfn-bony-price]').textContent = subscriptionPrice;
    facade.querySelector('[data-dfn-bony-compare]').textContent = comparePrice && comparePrice !== subscriptionPrice ? `statt ${comparePrice}` : '';
    facade.querySelector('[data-dfn-bony-button-text]').textContent = subscriptionSelected ? 'Abo-Einstellungen' : 'Abo auswählen';
    facade.querySelector('[data-dfn-bony-one-time]').hidden = !subscriptionSelected;
    host.classList.toggle('is-dfn-bony-selected', subscriptionSelected);
  }

  function initializeBonySubscriptions(productInfo) {
    productInfo.querySelectorAll('.bs-widget-wrapper').forEach((wrapper) => {
      if (wrapper.dataset.dfnBonyFacade === 'true') return;
      const identified = identifyBonyItems(wrapper);
      if (!identified) return;
      wrapper.dataset.dfnBonyFacade = 'true';
      const host = wrapper.closest('[data-dfn-app-block]') || wrapper.parentElement;
      host.querySelector(':scope > .dfn-bony-facade')?.remove();
      buildBonyFacade(host, wrapper);
    });

    if (productInfo.dataset.dfnBonyObserver !== 'true') {
      productInfo.dataset.dfnBonyObserver = 'true';
      let frame = 0;
      new MutationObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => initializeBonySubscriptions(productInfo));
      }).observe(productInfo, { childList: true, subtree: true });
    }
  }

  function findNativeSubscriptionWidget(productInfo) {
    return productInfo.querySelector(
      '.bs-widget-wrapper, [data-bs-widget], .bs-subscription-widget-block'
    );
  }

  function prepareNativeSubscriptionPanel(productInfo, preview) {
    const widget = findNativeSubscriptionWidget(productInfo);
    if (!widget) return null;

    const identified = identifyBonyItems(widget);
    if (!identified?.subscription) return null;

    const panel = widget.closest('[data-dfn-app-block]') || widget;
    panel.classList.add('dfn-native-subscription-panel');
    panel.dataset.dfnNativeSubscriptionPanel = 'true';
    if (preview.nextElementSibling !== panel) {
      preview.insertAdjacentElement('afterend', panel);
    }

    widget.classList.add('dfn-native-subscription-widget');
    widget.querySelectorAll('.bs-title').forEach((title) => {
      title.hidden = true;
      title.setAttribute('aria-hidden', 'true');
    });

    identified.items.forEach((item) => {
      item.classList.remove(
        'dfn-native-subscription-option',
        'dfn-native-one-time-option'
      );
    });
    identified.oneTime?.classList.add('dfn-native-one-time-option');
    identified.subscriptions.forEach((item) => item.classList.add('dfn-native-subscription-option'));

    // Keine Abo-Optionen per append()/prepend() umsortieren. Bei mehreren Laufzeiten
    // würde das zusammen mit Bonys DOM-Updates einen Mutation-Loop auslösen.
    compactSubscriptionEuroNodes(panel);
    compactSubscriptionEuroNodes(preview);
    verticallyCenterNativeSubscriptionOptions(panel);

    const selectedSubscription = identified.subscriptions.find((item) => nativeBonyItemIsSelected(item)) || null;
    const subscriptionSelected = Boolean(selectedSubscription);
    preview.classList.toggle('is-dfn-subscription-selected', subscriptionSelected);

    // Sobald eine konkrete Laufzeit gewählt wurde, spiegelt die kompakte Abo-Karte
    // genau diese Auswahl wider. Ohne Auswahl bleiben die Liquid-Startwerte stehen.
    if (selectedSubscription) {
      const selectedText = selectedSubscription.textContent.replace(/\s+/g, ' ').trim();
      const discountMatch = selectedText.match(/(\d{1,2})\s*%/);
      const selectedPrices = extractBonyPrices(selectedSubscription);
      const selectedPrice = selectedPrices[selectedPrices.length - 1] || '';
      const comparePrice = selectedPrices.length > 1 ? selectedPrices[0] : '';

      if (discountMatch) {
        const discount = discountMatch[1];
        const heading = preview.querySelector('.dfn-subscription-preview__heading h3');
        const benefit = preview.querySelector('.dfn-subscription-preview__benefits li:last-child');
        if (heading) heading.textContent = `Monatlich liefern & ${discount}% sparen`;
        if (benefit) benefit.textContent = `${discount}% Rabatt auf jede Lieferung`;
      }

      const priceNode = preview.querySelector('[data-dfn-subscription-price]');
      const compareNode = preview.querySelector('[data-dfn-subscription-compare]');
      if (priceNode && selectedPrice) priceNode.textContent = selectedPrice;
      if (compareNode && comparePrice && comparePrice !== selectedPrice) {
        compareNode.textContent = `statt ${comparePrice}`;
      }
    }

    const button = preview.querySelector('[data-dfn-subscription-preview-action]');
    const buttonText = button?.querySelector('span');
    if (buttonText) {
      buttonText.textContent = subscriptionSelected ? 'Abo bearbeiten' : 'Abo auswählen';
    }

    const oneTimeButton = preview.querySelector('[data-dfn-subscription-preview-one-time]');
    if (oneTimeButton) oneTimeButton.hidden = !subscriptionSelected;

    if (!panel.classList.contains('is-dfn-subscription-open')) {
      panel.setAttribute('aria-hidden', 'true');
    }

    return { widget, panel, identified };
  }

  function bindSubscriptionPreview(productInfo, preview) {
    const button = preview.querySelector('[data-dfn-subscription-preview-action]');
    const oneTimeButton = preview.querySelector('[data-dfn-subscription-preview-one-time]');
    if (!button) return;
    if (button.dataset.dfnSubscriptionPreviewBound !== 'true') {
      button.dataset.dfnSubscriptionPreviewBound = 'true';
      button.setAttribute('aria-expanded', 'false');

      button.addEventListener('click', () => {
        const current = prepareNativeSubscriptionPanel(productInfo, preview);
        if (!current) {
          let attempts = 0;
          const retry = window.setInterval(() => {
            attempts += 1;
            const delayed = prepareNativeSubscriptionPanel(productInfo, preview);
            if (!delayed && attempts < 20) return;
            window.clearInterval(retry);
            if (!delayed) return;
            openNativeSubscriptionPanel(preview, button, delayed);
          }, 100);
          return;
        }

        openNativeSubscriptionPanel(preview, button, current);
      });
    }

    if (oneTimeButton && oneTimeButton.dataset.dfnSubscriptionOneTimeBound !== 'true') {
      oneTimeButton.dataset.dfnSubscriptionOneTimeBound = 'true';
      oneTimeButton.addEventListener('click', () => {
        const current = prepareNativeSubscriptionPanel(productInfo, preview);
        if (!current?.identified?.oneTime) return;
        selectNativeBonyItem(current.identified.oneTime);
        current.panel.classList.remove('is-dfn-subscription-open');
        preview.classList.remove('is-dfn-subscription-open');
        current.panel.setAttribute('aria-hidden', 'true');
        button.setAttribute('aria-expanded', 'false');
        window.setTimeout(() => prepareNativeSubscriptionPanel(productInfo, preview), 100);
      });
    }

    prepareNativeSubscriptionPanel(productInfo, preview);
  }

  function openNativeSubscriptionPanel(preview, button, current) {
    const willOpen = !current.panel.classList.contains('is-dfn-subscription-open');
    current.panel.classList.toggle('is-dfn-subscription-open', willOpen);
    preview.classList.toggle('is-dfn-subscription-open', willOpen);
    current.panel.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
    button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

    if (willOpen) {
      window.setTimeout(() => {
        const focusSubscription =
          current.identified.subscriptions?.find((item) => nativeBonyItemIsSelected(item)) ||
          current.identified.subscription;
        const focusTarget = focusSubscription?.querySelector(
          'input, button, select, [tabindex]:not([tabindex="-1"])'
        );
        focusTarget?.focus({ preventScroll: true });
      }, 220);
    }
  }

  function initializeSubscriptionPreview(productInfo) {
    const previews = [...productInfo.querySelectorAll('[data-dfn-subscription-preview]')];
    if (!previews.length) return;

    previews.forEach((preview) => bindSubscriptionPreview(productInfo, preview));

    if (productInfo.dataset.dfnNativeSubscriptionObserved === 'true') return;
    productInfo.dataset.dfnNativeSubscriptionObserved = 'true';

    let frame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        productInfo.querySelectorAll('[data-dfn-subscription-preview]').forEach((preview) => {
          bindSubscriptionPreview(productInfo, preview);
        });
      });
    }).observe(productInfo, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-selected', 'aria-checked', 'checked', 'value'],
    });
  }

  function localizeReviewBadge(scope) {
    if (document.documentElement.lang && !document.documentElement.lang.toLowerCase().startsWith('de')) return;

    scope.querySelectorAll?.('.jdgm-prev-badge__text').forEach((node) => {
      const raw = node.textContent.replace(/\s+/g, ' ').trim();
      let localized = '';

      if (/no reviews?|not reviewed|0 reviews?/i.test(raw)) {
        localized = 'Noch keine Bewertungen';
      } else if (/be the first|first to review/i.test(raw)) {
        localized = 'Sei der Erste, der bewertet';
      } else {
        const match = raw.match(/\d[\d.,]*/);
        if (!match) return;
        const countText = match[0];
        const numericCount = Number(countText.replace(/\./g, '').replace(',', '.'));
        localized = `${countText} ${numericCount === 1 ? 'Bewertung' : 'Bewertungen'}`;
      }

      if (raw !== localized) node.textContent = localized;
      const badge = node.closest('.jdgm-prev-badge');
      if (badge) badge.setAttribute('aria-label', localized);
    });
  }

  function initializeJudgeMeMedalScroller(scope) {
    scope.querySelectorAll?.('.jdgm-medals').forEach((scroller) => {
      scroller.classList.add('dfn-judgeme-medals-scroll');
      if (scroller.dataset.dfnDragScrollBound === 'true') return;

      scroller.dataset.dfnDragScrollBound = 'true';

      const updateScrollableState = () => {
        scroller.classList.toggle('is-dfn-scrollable', scroller.scrollWidth > scroller.clientWidth + 2);
      };
      requestAnimationFrame(updateScrollableState);
      if ('ResizeObserver' in window) {
        new ResizeObserver(updateScrollableState).observe(scroller);
      } else {
        window.addEventListener('resize', updateScrollableState, { passive: true });
      }

      let isDragging = false;
      let pointerId = null;
      let startX = 0;
      let startScrollLeft = 0;
      let suppressClick = false;

      scroller.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (scroller.scrollWidth <= scroller.clientWidth + 2) return;

        isDragging = true;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = scroller.scrollLeft;
        suppressClick = false;
        scroller.classList.add('is-dfn-dragging');
        scroller.setPointerCapture?.(event.pointerId);
      });

      scroller.addEventListener('pointermove', (event) => {
        if (!isDragging || event.pointerId !== pointerId) return;

        const delta = event.clientX - startX;
        if (Math.abs(delta) > 3) {
          suppressClick = true;
          event.preventDefault();
        }
        scroller.scrollLeft = startScrollLeft - delta;
      });

      const stopDragging = (event) => {
        if (!isDragging) return;
        if (event?.pointerId != null && pointerId != null && event.pointerId !== pointerId) return;

        isDragging = false;
        scroller.classList.remove('is-dfn-dragging');
        if (pointerId != null && scroller.hasPointerCapture?.(pointerId)) {
          scroller.releasePointerCapture(pointerId);
        }
        pointerId = null;
      };

      scroller.addEventListener('pointerup', stopDragging);
      scroller.addEventListener('pointercancel', stopDragging);
      scroller.addEventListener('lostpointercapture', stopDragging);

      scroller.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);
    });
  }

  function enhanceReviewWidget(scope) {
    const panel = scope.matches?.('.dfn-tabs__panel--reviews')
      ? scope
      : scope.querySelector?.('.dfn-tabs__panel--reviews');
    if (!panel) return;

    panel.querySelectorAll('.jdgm-rev').forEach((review) => {
      const candidates = [...review.querySelectorAll('a, span, p, div')]
        .map((node) => ({ node, text: node.textContent.replace(/\s+/g, ' ').trim() }))
        .filter(({ text }) => text && text.length <= 180)
        .sort((a, b) => a.text.length - b.text.length);

      const source = candidates.find(({ text }) =>
        /bewertung gesammelt durch eine einladung zum shop|review collected .* invitation/i.test(text)
      )?.node;
      if (source) source.classList.add('dfn-review-source-note');

      const collectionLink = candidates.find(({ node, text }) =>
        node.matches('a') && /wie werden bewertungen gesammelt\?|how are reviews collected\?/i.test(text)
      )?.node;

      const incentive = candidates.find(({ text }) =>
        /bewerter erhielt einen rabattcode|reviewer received a discount code|discount code for a future order/i.test(text)
      )?.node;
      if (incentive) incentive.classList.add('dfn-review-incentive-note');

      if (collectionLink) {
        collectionLink.classList.add('dfn-review-collection-link');
        const previous = collectionLink.previousElementSibling;
        if (!previous?.classList.contains('dfn-review-collection-divider')) {
          const divider = document.createElement('span');
          divider.className = 'dfn-review-collection-divider';
          divider.setAttribute('aria-hidden', 'true');
          collectionLink.before(divider);
        }
      }
    });

    initializeJudgeMeMedalScroller(panel);
  }

  function initializeReviewLocalization(productInfo) {
    localizeReviewBadge(productInfo);
    enhanceReviewWidget(productInfo);
    if (productInfo.dataset.dfnReviewLocalizationObserved === 'true') return;

    productInfo.dataset.dfnReviewLocalizationObserved = 'true';
    let frame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        localizeReviewBadge(productInfo);
        enhanceReviewWidget(productInfo);
      });
    }).observe(productInfo, { childList: true, subtree: true, characterData: true });
  }

  function initializeProductInfo(productInfo) {
    if (!productInfo) return;
    initializeTabs(productInfo);
    initializeAccessoryCards(productInfo);
    initializeAccessoryPagination(productInfo);
    initializeMediaDots(productInfo);
    initializeThumbnailSliderArrows(productInfo);
    initializeSubscriptionPreview(productInfo);
    initializeReviewLocalization(productInfo);
    refreshBestsellerBadges(productInfo);
    refreshSubscriptionPreview(productInfo);
    updateVariantIdentifiers(productInfo);
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll?.('product-info.dfn-product-page').forEach(initializeProductInfo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('product-info:loaded', (event) => initializeProductInfo(event.target));
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  document.addEventListener('change', (event) => {
    if (!event.target.matches('.product-variant-id, variant-selects input, variant-selects select')) return;
    const productInfo = event.target.closest('product-info.dfn-product-page');
    if (!productInfo) return;
    window.setTimeout(() => refreshBestsellerBadges(productInfo), 0);
    window.setTimeout(() => {
      refreshSubscriptionPreview(productInfo);
      updateVariantIdentifiers(productInfo);
    }, 80);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = [...document.querySelectorAll('product-info.dfn-product-page')]
        .find((node) => String(node.dataset.section) === String(data?.sectionId));
      if (!productInfo) return;

      refreshBestsellerBadges(productInfo);
      refreshSubscriptionPreview(productInfo, data?.variant || null);
      updateVariantIdentifiers(productInfo, data?.variant || null);
    });
  }
})();
